import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Fade,
  Zoom,
  Grid,
  TextField,
  Link,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import api from "../../api/axiosInterceptor";
import { API_BASE_URL } from "../../config";

// Read-only info field
function InfoField({ label, value }) {
  return (
    <TextField
      label={label}
      value={value || "-"}
      variant="outlined"
      fullWidth
      InputProps={{
        readOnly: true,
        sx: { bgcolor: "#fafafa" },
      }}
    />
  );
}

// Label and View link for documents
function InfoLink({ label, url }) {
  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary">
        {label}
      </Typography>
      {url ? (
        <Link href={url} target="_blank" rel="noopener noreferrer">
          View
        </Link>
      ) : (
        "-"
      )}
    </Box>
  );
}

// Reusable Table
function SectionTable({ title, columns, data }) {
  if (!data || data.length === 0)
    return <Typography color="text.secondary">No data available.</Typography>;

  return (
    <Box mb={3}>
      <Fade in timeout={500}>
        <Paper
          sx={{
            overflowX: "auto",
            borderRadius: 3,
            boxShadow: "0 6px 24px rgba(0,0,0,0.07)",
          }}
        >
          {title && (
            <Typography variant="h6" sx={{ p: 2, pb: 0 }}>
              {title}
            </Typography>
          )}
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f3f5f7" }}>
                {columns.map((col) => (
                  <TableCell key={col.key}>{col.header}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((item, idx) => (
                <TableRow
                  key={idx}
                  hover
                  sx={{
                    transition: "background 0.3s, transform 0.3s",
                    "&:hover": {
                      backgroundColor: "#eaf3ff",
                      transform: "scale(1.01)",
                    },
                  }}
                >
                  {columns.map((col) => (
                    <TableCell key={col.key}>
                      {col.render ? col.render(item) : item[col.key] || "-"}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Fade>
    </Box>
  );
}

export default function ClaimDetailPage({ pk, claim_url }) {
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchClaim() {
      setLoading(true);
      try {
        const res = await api.get(`/claim-mgmt/${claim_url}/${pk}/`);
        console.log(res.data)
        setClaim(res.data);
      } catch (e) {
        console.error("Error loading claim:", e);
        setClaim(null);
      } finally {
        setLoading(false);
      }
    }
    fetchClaim();
  }, [pk]);

  if (loading)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );

  if (!claim)
    return (
      <Typography align="center" sx={{ mt: 4 }}>
        No claim found.
      </Typography>
    );

  // Determine claim status
  const csb = Array.isArray(claim.claim_status_base)
    ? claim.claim_status_base[0]
    : claim.claim_status_base;
  const status = csb
    ? csb.is_rejected
      ? "Rejected"
      : csb.is_settled
      ? "Settled"
      : csb.is_ca_verified
      ? "CA Verified"
      : csb.is_verified_2 || csb.is_verified_1
      ? "In Progress"
      : csb.is_submited
      ? "Submitted"
      : "Draft"
    : "Unknown";

  const statusColor =
    status === "Rejected"
      ? "error"
      : status.includes("Verified")
      ? "success"
      : status === "Submitted"
      ? "primary"
      : "warning";

  return (
    <Box
      sx={{
        p: 4,
        background: "linear-gradient(135deg, #e3f2fd 10%, #f1f8e9 90%)",
        minHeight: "100vh",
      }}
    >
      <Zoom in timeout={600}>
        <Box>
          <Card
            sx={{
              bgcolor: "background.paper",
              borderRadius: 4,
              boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
              mb: 3,
            }}
          >
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
                Claim Details
              </Typography>

              {/* Acknowledge No at top in bold */}
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                Acknowledgment No: {claim.ack_no || "-"}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Chip label={status} color={statusColor} sx={{ mb: 3 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <InfoField label="Claimant" value={claim.name_of_claimant1} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoField label="FE Name" value={claim.fe_name} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoField
                    label="Applied Date"
                    value={new Date(claim.created).toLocaleDateString()}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoField
                    label="Father/Husband Name"
                    value={claim.father_husband_name}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoField label="Date of Birth" value={claim.date_of_birth} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoField label="Phone No." value={claim.ph_no} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoField label="Email 1" value={claim.email_1} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoField label="Email 2" value={claim.email_2} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoField label="PAN No." value={claim.pan_no} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoField label="FE Code" value={claim.fe_code} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoField label="CA Designation" value={claim.ca_designation} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoField label="Is Delayed?" value={claim.is_delayed ? "Yes" : "No"} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoField label="Is Normal?" value={claim.is_normal ? "Yes" : "No"} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoField label="Claimant Type" value={claim.claiment_type} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoField label="Account Type" value={claim.account_type} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoField
                    label="Relation With Owner"
                    value={claim.relation_with_owner}
                  />
                </Grid>
                <Grid item xs={12}>
                  <InfoField label="Address" value={claim.address} />
                </Grid>

                {/* Documents at bottom */}
                <Grid item xs={12} sm={4}>
                  <InfoLink label="PAN Document" url={claim.pan} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InfoLink label="eKYC Document" url={claim.ekyc_doc} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InfoLink label="Photo" url={claim.photo} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Accordions */}

          

{/* adahr */}
  <Accordion>
  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
    <Typography fontWeight={700} fontSize={16}>
      Aadhaar E-KYC Verification
    </Typography>
  </AccordionSummary>

  <AccordionDetails>
    {!claim?.adhaar_verification ||
    claim.adhaar_verification.length === 0 ? (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          textAlign: "center",
          borderRadius: 3,
          background: "#f8f9fa",
          border: "1px dashed #bdbdbd",
        }}
      >
        <Typography fontWeight={600} color="text.secondary">
          Verified using Different Method
        </Typography>
      </Paper>
    ) : (
      claim.adhaar_verification.map((item, index) => {
        const ekyc = item?.ekyc_data || {};

        const fullAddress = [
          ekyc.house,
          ekyc.street,
          ekyc.loc,
          ekyc.vtc,
          ekyc.dist,
          ekyc.state,
          ekyc.pc,
          ekyc.country,
        ]
          .filter(Boolean)
          .join(", ");

        return (
          <Paper
            key={item.id || index}
            elevation={2}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 4,
              border: "1px solid #e0e0e0",
              overflow: "hidden",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                mb: 3,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Typography fontWeight={700} fontSize={18}>
                Aadhaar Verification Details
              </Typography>

              <Box
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  background:
                    Number(item.name_matching_percentage) >= 80
                      ? "#e8f5e9"
                      : "#fff3e0",
                  color:
                    Number(item.name_matching_percentage) >= 80
                      ? "#2e7d32"
                      : "#ef6c00",
                  fontWeight: 700,
                  fontSize: 14,
                }}
              >
                Name Match: {item.name_matching_percentage || 0}%
              </Box>
            </Box>

            <Grid container spacing={3}>
              {/* Photo */}
              <Grid item xs={12} md={3}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  {item.Photo ? (
                    <img
                      src={`data:image/jpeg;base64,${item.Photo}`}
                      alt="Aadhaar"
                      style={{
                        width: "100%",
                        maxWidth: 190,
                        borderRadius: 12,
                        border: "1px solid #ddd",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 180,
                        height: 220,
                        borderRadius: 3,
                        border: "1px dashed #ccc",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "text.secondary",
                      }}
                    >
                      No Photo
                    </Box>
                  )}
                </Box>
              </Grid>

              {/* Details */}
              <Grid item xs={12} md={9}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      value={ekyc.name || ""}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Date of Birth"
                      value={ekyc.dob || ""}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Father / Guardian Name"
                      value={ekyc.co || ""}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Masked Aadhaar"
                      value={item.maskedAadhaar || ""}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Gender"
                      value={ekyc.gender || ""}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Verification Status"
                      value={item.status || ""}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      label="Address"
                      value={fullAddress}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        );
      })
    )}
  </AccordionDetails>
</Accordion>

          {/* delay */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={600}>Delay Court Order</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <SectionTable
                columns={[
                  { key: "ord_no", header: "Court Order No" },
                  {
                    key: "proof",
                    header: "Proof",
                    render: (row) =>
                      row.proof ? (
                        <Link href={row.proof} target="_blank" rel="noreferrer">
                          View
                        </Link>
                      ) : (
                        "-"
                      ),
                  },
                ]}
                data={claim.delayed_application_docs}
              />
            </AccordionDetails>
          </Accordion>




          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={600}>Affidavit</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <SectionTable
                columns={[
                  {
                    key: "doc1",
                    header: "Document",
                    render: (row) =>
                      row.doc1 ? (
                        <Link href={row.doc1} target="_blank" rel="noreferrer">
                          View
                        </Link>
                      ) : (
                        "-"
                      ),
                  },
                ]}
                data={claim.affidavit}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={600}>Customer</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <SectionTable
                columns={[
                  { key: "cust_id", header: "Customer ID" },
                  {
                    key: "doc",
                    header: "Document",
                    render: (row) =>
                      row.doc ? (
                        <Link href={row.doc} target="_blank" rel="noreferrer">
                          View
                        </Link>
                      ) : (
                        "-"
                      ),
                  },
                ]}
                data={claim.cust}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={600}>Loans</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <SectionTable
                columns={[
                  { key: "account_no", header: "Account No" },
                  { key: "amt_borrowed", header: "Amount Borrowed" },
                  { key: "amt_paid", header: "Amount Paid" },
                  { key: "amt_remaining", header: "Remaining" },
                  { key: "closed_date", header: "Closed Date" },
                  {
                    key: "proof",
                    header: "Proof",
                    render: (row) =>
                      row.proof ? (
                        <Link href={row.proof} target="_blank" rel="noreferrer">
                          View
                        </Link>
                      ) : (
                        "-"
                      ),
                  },
                ]}
                data={claim.loans}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={600}>Investments</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <SectionTable
                columns={[
                  { key: "account_no", header: "Account No" },
                  { key: "bank_name", header: "Bank Name" },
                  { key: "scheme", header: "Scheme" },
                  { key: "amt", header: "Amount" },
                  // { key: "mode_of_payment", header: "Payment Mode" },
                  {
                    key: "proof",
                    header: "Proof",
                    render: (row) =>
                      row.proof ? (
                        <Link href={row.proof} target="_blank" rel="noreferrer">
                          View
                        </Link>
                      ) : (
                        "-"
                      ),
                  },
                ]}
                data={claim.investments}
              />
            </AccordionDetails>
          </Accordion>
<Accordion>
  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
    <Typography fontWeight={600}>Deposited Details</Typography>
  </AccordionSummary>
  <AccordionDetails>
    <SectionTable
      columns={[
        { key: "acc_no", header: "Account No" },
        { key: "bank_name", header: "Bank Name" },
        { key: "branch_name", header: "Branch" },
        { key: "ifsc_code", header: "IFSC Code" },
        { key: "deposted_amt", header: "Deposited Amount" },
        { key: "date_of_deposit", header: "Date of Deposit" },
        { key: "particulars", header: "Mode of Payment" },
        { key: "type_of_scheme", header: "Scheme Type" },
        { key: "remark", header: "Remark" },
        {
          key: "statement_doc",
          header: "Statement Document",
          render: (row) =>
            row.statement_doc ? (
              <Link href={row.statement_doc} target="_blank" rel="noreferrer">
                View
              </Link>
            ) : (
              "-"
            ),
        },
      ]}
      data={claim.depositedBanks}
    />
  </AccordionDetails>
</Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={600}>Returned Funds</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <SectionTable
                columns={[
                  { key: "account_no", header: "Account No" },
                  { key: "bank_name", header: "Bank Name" },
                  { key: "branch_name", header: "Branch" },
                  { key: "scheme", header: "Scheme" },
                  { key: "amt", header: "Amount" },
                  { key: "mode_of_payment", header: "Payment Mode" },
                  {
                    key: "proof",
                    header: "Proof",
                    render: (row) =>
                      row.proof ? (
                        <Link href={row.proof} target="_blank" rel="noreferrer">
                          View
                        </Link>
                      ) : (
                        "-"
                      ),
                  },
                ]}
                data={claim.returneds}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={600}>Refund Bank Details</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <SectionTable
                columns={[
                  { key: "holder_name", header: "Holder Name" },
                  { key: "rel_with_nominee", header: "Relation" },
                  { key: "acc_no", header: "Account No" },
                  { key: "bank_name", header: "Bank Name" },
                  { key: "branch_name", header: "Branch" },
                  { key: "ifsc_code", header: "IFSC" },
                  {
                    key: "front_sheet",
                    header: "Front Sheet",
                    render: (row) =>
                      row.front_sheet ? (
                        <Link href={row.front_sheet} target="_blank" rel="noreferrer">
                          View
                        </Link>
                      ) : (
                        "-"
                      ),
                  },
                ]}
                data={claim.refundBank}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={600}>Legal Heir</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <SectionTable
                columns={[

                  { key: "name", header: "Name" },
                    { key: "gender", header: "Gender" },
                    { key: "relation_with_depositor", header: "Relation" },
                    {
                      key: "pan",
                      header: "PAN",
                      render: (row) =>
                        row.pan ? (
                          <Link href={row.pan} target="_blank" rel="noreferrer">
                            View
                          </Link>
                        ) : (
                          "-"
                        ),
                    },
                    {
                      key: "ekyc",
                      header: "eKYC",
                      render: (row) =>
                        row.ekyc ? (
                          <Link href={row.ekyc} target="_blank" rel="noreferrer">
                            View
                          </Link>
                        ) : (
                          "-"
                        ),
                    },
                    {
                      key: "death_certificate",
                      header: "Death Certificate",
                      render: (row) =>
                        row.death_certificate ? (
                          <Link href={row.death_certificate} target="_blank" rel="noreferrer">
                            View
                          </Link>
                        ) : (
                          "-"
                        ),
                    },

                ]}
                data={claim.legalHeir}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={600}>Nominee</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <SectionTable
                columns={[
                  { key: "name", header: "Name" },
                  { key: "gender", header: "Gender" },
                  { key: "rel_with_depositor", header: "Relation" },
                  {
                    key: "death_cert",
                    header: "Death Certificate",
                    render: (row) =>
                      row.death_cert ? (
                        <Link href={row.death_cert} target="_blank" rel="noreferrer">
                          View
                        </Link>
                      ) : (
                        "-"
                      ),
                  },
                  {
                    key: "family_tree",
                    header: "Family Tree",
                    render: (row) =>
                      row.family_tree ? (
                        <Link href={row.family_tree} target="_blank" rel="noreferrer">
                          View
                        </Link>
                      ) : (
                        "-"
                      ),
                  },
                  {
                    key: "e_kyc_doc",
                    header: "eKYC Document",
                    render: (row) =>
                      row.e_kyc_doc ? (
                        <Link href={row.e_kyc_doc} target="_blank" rel="noreferrer">
                          View
                        </Link>
                      ) : (
                        "-"
                      ),
                  },
                  {
                    key: "pan",
                    header: "PAN",
                    render: (row) =>
                      row.pan ? (
                        <Link href={row.pan} target="_blank" rel="noreferrer">
                          View
                        </Link>
                      ) : (
                        "-"
                      ),
                  },
                  {
                    key: "nomination_proof",
                    header: "Nomination Proof",
                    render: (row) =>
                      row.nomination_proof ? (
                        <Link href={row.nomination_proof} target="_blank" rel="noreferrer">
                          View
                        </Link>
                      ) : (
                        "-"
                      ),
                  },
                ]}
                data={claim.nominee}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={600}>Summary</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <SectionTable
                columns={[
                  { key: "total_deposited", header: "Total Deposited" },
                  { key: "total_received", header: "Total Received" },
                  { key: "total_loan", header: "Total Loans" },
                  { key: "total_claim", header: "Total Claim" },
                ]}
                data={claim.total}
              />
            </AccordionDetails>
          </Accordion>
        </Box>
      </Zoom>
    </Box>
  );
}
